import React from 'react';

/**
 * Loading Skeleton Component
 * Provides skeleton screens for content loading
 * Helps improve perceived performance
 */
export default function LoadingSkeleton({
  type = 'text',
  width,
  height,
  count = 1,
  className = '',
  style = {},
  ...rest
}) {
  const baseStyle = {
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    animation: 'skeleton-loading 1.5s ease-in-out infinite',
    ...style,
  };

  const renderSkeleton = () => {
    switch (type) {
      case 'text':
        return (
          <div
            style={{
              ...baseStyle,
              height: height || '16px',
              width: width || '100%',
              marginBottom: count > 1 ? '8px' : 0,
            }}
            className={`skeleton skeleton-text ${className}`}
            {...rest}
          />
        );

      case 'circle':
        return (
          <div
            style={{
              ...baseStyle,
              width: width || height || '48px',
              height: height || width || '48px',
              borderRadius: '50%',
            }}
            className={`skeleton skeleton-circle ${className}`}
            {...rest}
          />
        );

      case 'rect':
        return (
          <div
            style={{
              ...baseStyle,
              width: width || '100%',
              height: height || '200px',
            }}
            className={`skeleton skeleton-rect ${className}`}
            {...rest}
          />
        );

      case 'card':
        return (
          <div
            style={{
              ...baseStyle,
              width: width || '100%',
              height: height || '300px',
              borderRadius: '8px',
            }}
            className={`skeleton skeleton-card ${className}`}
            {...rest}
          />
        );

      case 'avatar':
        return (
          <div
            style={{
              ...baseStyle,
              width: width || '48px',
              height: height || '48px',
              borderRadius: '50%',
            }}
            className={`skeleton skeleton-avatar ${className}`}
            {...rest}
          />
        );

      default:
        return (
          <div
            style={{
              ...baseStyle,
              width: width || '100%',
              height: height || '16px',
            }}
            className={`skeleton ${className}`}
            {...rest}
          />
        );
    }
  };

  if (count > 1) {
    return (
      <>
        <style>{`
          @keyframes skeleton-loading {
            0% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
            100% {
              opacity: 1;
            }
          }
        `}</style>
        {Array.from({ length: count }).map((_, index) => (
          <React.Fragment key={index}>
            {renderSkeleton()}
          </React.Fragment>
        ))}
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes skeleton-loading {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            opacity: 1;
          }
        }
      `}</style>
      {renderSkeleton()}
    </>
  );
}

/**
 * Card Skeleton Component
 * Pre-built skeleton for cards
 */
export function CardSkeleton({ count = 1 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <LoadingSkeleton type="avatar" width={48} height={48} />
            <div style={{ marginLeft: '12px', flex: 1 }}>
              <LoadingSkeleton type="text" width="60%" height={16} />
              <LoadingSkeleton type="text" width="40%" height={14} count={1} style={{ marginTop: '8px' }} />
            </div>
          </div>
          <LoadingSkeleton type="text" count={3} />
          <LoadingSkeleton type="rect" height={120} style={{ marginTop: '12px' }} />
        </div>
      ))}
    </>
  );
}

/**
 * Page Skeleton Component
 * Pre-built skeleton for pages
 */
export function PageSkeleton() {
  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <LoadingSkeleton type="text" width="40%" height={32} style={{ marginBottom: '24px' }} />
      <LoadingSkeleton type="text" width="80%" height={16} count={2} />
      <LoadingSkeleton type="rect" height={300} style={{ marginTop: '24px' }} />
      <CardSkeleton count={3} />
    </div>
  );
}







